import React, { useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';

import { ComponentPreview, useElementRect } from '@paragon/client-tools';
import { Truss } from '@paragon/contract-library';
import { calculateMemberGeometry } from '@paragon/relative-geometry';
import { notNullish } from '@paragon/utilities';

/** Keeps a wide, shallow truss from taking over the panel while still leaving a tall one readable. */
const MAX_PREVIEW_HEIGHT = 140;
/** Used until the container has been measured, so the first paint is not a zero-sized canvas. */
const FALLBACK_PREVIEW_WIDTH = 240;

interface Props {
  readonly title: string;
  readonly truss: Truss;
}

/**
 * Draws a truss the optimizer has produced, sized to whatever width the panel gives it.
 */
export function BestTrussPreview({ title, truss }: Props) {
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const containerRect = useElementRect(container);

  const memberVertices = useMemo(
    () => truss.members.map((member) => calculateMemberGeometry(member, truss)).filter(notNullish),
    [truss],
  );

  const width = containerRect != null && containerRect.width > 0 ? containerRect.width : FALLBACK_PREVIEW_WIDTH;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      <Typography variant="caption" color="text.secondary">
        {title}
      </Typography>
      <Box ref={setContainer} sx={{ display: 'flex', justifyContent: 'center', minHeight: 24 }}>
        <ComponentPreview memberVertices={memberVertices} maxWidth={width} maxHeight={MAX_PREVIEW_HEIGHT} />
      </Box>
    </Box>
  );
}
